import { useState, useMemo, useEffect, useCallback } from "react";
import * as yup from "yup";
import { omit } from "lodash";
import DateTimeRangeSelector, {
  predefinedRanges,
} from "@8x8/oxygen-date-time-range-selector";

import Button from "@8x8/oxygen-button";
import EnvelopeIcon from "@8x8/oxygen-icon/novo/EnvelopeIcon";

import Input from "@/react/components/Input";
import TableComponent from "@/react/components/table/TableComponent";
import { useTable } from "@/react/components/table/useTable";
import { InnerContainer } from "@/react/features/support-tools/common/styled";
import { toUnixTimestamp } from "@/react/datetime";
import { EMPTY_ARRAY } from "@/react/empty";

import { FiltersRow, SearchContainer } from "./styled";
import { useLazyGetEmailsQuery } from "./api";
import { initialRange, parseDateRange } from "./date";
import schema from "./schema";
import columnDefinition from "./columnDefinition";
import PageTitle from "@/react/components/PageTitle";
import PageLayout from "@/react/features/styled/PageLayout";
import TableWrapper from "@/react/features/styled/TableWrapper";

const buildPayload = (email, dateTimeRange = null) => {
  const payload = { email };
  if (dateTimeRange?.startDate) {
    payload.startDate = toUnixTimestamp(dateTimeRange?.startDate);
  }
  if (dateTimeRange?.endDate) {
    payload.endDate = toUnixTimestamp(dateTimeRange?.endDate);
  }

  return payload;
};
const computeIsLoading = (isLoading, status) => {
  return isLoading === true || status === "pending";
};
const columns = columnDefinition();

const EmailStatusPage = () => {
  const [dateTimeRange, setDateTimeRange] = useState(initialRange);
  const [email, setEmail] = useState("");
  const [getEmails, result] = useLazyGetEmailsQuery();
  const {
    data: emailData,
    isLoading: isRequestLoading,
    status: requestStatus,
  } = result;
  const isLoading = computeIsLoading(isRequestLoading, requestStatus);
  const { table, filters, state, mutations } = useTable(
    emailData?.messages ?? EMPTY_ARRAY,
    columns,
    schema,
    yup.reach(schema, "msg_id").spec.key
  );

  const handleSearch = () => {
    const payload = buildPayload(email, dateTimeRange);
    getEmails(payload);
  };
  const onRangeChange = useCallback(
    (data) => {
      setDateTimeRange(parseDateRange(data));
    },
    [setDateTimeRange]
  );

  // Register an effect to trigger a search when the date time range changes,
  // since it is not correct to do it right after calling setDateTimeRange.
  useEffect(() => {
    handleSearch();
  }, [dateTimeRange]);

  // Remove add and delete operations from the final set of possible mutations,
  // since those are not allowed by this API.
  const finalMutations = useMemo(
    () => omit(mutations, ["addRows", "deleteRows"]),
    [mutations]
  );
  const fileActions = useMemo(
    () => ({
      identifierColumnId: yup.reach(schema, "msg_id").spec.key,
      enableUpdate: false,
      enableAdd: false,
      enableDownload: true,
    }),
    []
  );

  return (
    <PageLayout>
      <PageTitle>Email Delivery Status Check</PageTitle>
      <InnerContainer>
        <FiltersRow>
          <SearchContainer>
            <Input
              placeholder="Enter an email, e.g. abc@example.com"
              value={email}
              setValue={setEmail}
              onSubmit={handleSearch}
              submitOnEnter
              fullWidth
            />
            <Button onClick={handleSearch}>
              <EnvelopeIcon invertColors />
              Check
            </Button>
          </SearchContainer>
          <DateTimeRangeSelector
            predefinedRanges={predefinedRanges}
            dateTimeRange={dateTimeRange}
            placeholder="Select a date range for the search"
            hideTime={true}
            dateFormat="dd MMM yyyy"
            onRangeChange={onRangeChange}
          />
        </FiltersRow>
        <TableWrapper>
          <TableComponent
            title="Email Delivery Status Check"
            schema={schema}
            table={table}
            onRefresh={handleSearch}
            filters={filters}
            state={state}
            mutations={finalMutations}
            isLoading={isLoading}
            fileActions={fileActions}
          />
        </TableWrapper>
      </InnerContainer>
    </PageLayout>
  );
};
export default EmailStatusPage;
